import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Icon, Input, Message, Popup, Progress, Step } from 'semantic-ui-react';
import { useDebounce } from 'use-lodash-debounce';
import MilkMoneyAccountingService, { FarmerInvoiceType } from '~/api/services/milkMoneyAccounting';
import Dialog from '~/components/Dialog';
import ExcelExport from '~/components/ExcelExport';
import Layout from '~/components/Layout';
import { LazyLottie } from '~/components/LazyLottie';
import notify from '~/components/Notify';
import TableComponent from '~/components/TableComponent';
import { useHash } from '~/components/useHash';
import { LIGHT_USER_ROLE_SLUG } from '~/Constants';
import ENV from '~/environment';
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery';
import { useAuth } from '~/routes/components/AuthProvider';
import { handleErrors, parseDate } from '~/utils/helpers';
import { FilterWrapper } from '../../DairyFactory/Farmers/style';
import { milkPaymentStatus } from '../Invoice/constants';
import { ActionsWrapper, ProgressWrapper, StepDescr, StyledDimmer } from '../Invoice/style';
import { actions, getSteps } from '../PartPayment/constants';
import CreateBankFile from '../PartPayment/CreateBankFile';
import CloseMonth from './CloseMonth';
import { getTableColumnHeaders } from './constants';
import GenerateModal from './GenerateModal';
import Report from './Report';

const AdditionalPayment: React.FC = () => {
  const { t } = useTranslation()
  const increment = useRef(null);
  const intervalPdfs = useRef(null);
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lighUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const steps = getSteps()
  const [hash] = useHash();

  const [tableColumnHeaders, setTableColumnHeaders] = useState(getTableColumnHeaders())
  const [isOpenCloseMonthDialog, setIsOpenCloseMonthDialog] = useState(false)
  const [dataFiltered, setDataFiltered] = useState({
    periodId: '',
    search: '',
    page: 0,
    rows: 20
  })
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [search, setSearch] = useState('')
  const [isLoadingPdfFiles, setIsLoadingPdfFiles] = useState(false)
  const [periodId, setPeriodId] = useState('')
  const [openExportBankModal, setExportBankModal] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [isBusySepaXML, setIsBusySepaXML] = useState(false)
  const [isOpenGeneratingPdfs, setIsOpenGeneratingPdfs] = useState(false)
  const [openGeneratingModal, setOpenGeneratingModal] = useState(false)

  const debouncedValue = useDebounce(search, 300);

  const {
    data: additionalPaymentSettings,
    isLoading: isBusySettings,
    isFetching: isFetchingSettings,
    refetch: fetchSettings
  } = MilkMoneyQuery.getAdditionalInvoiceSettings({
    periodId
  })

  const invoiceInDefaultStatus = additionalPaymentSettings?.status >= milkPaymentStatus.verificationStatus

  const generatingFile = additionalPaymentSettings?.status === milkPaymentStatus.generatingStatus

  const {
    data: additionalInvoices,
    isLoading: isBusy,
    refetch: fetchInvoices,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getAdditionalInvoices(
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
      periodId
    },
    invoiceInDefaultStatus && Boolean(periodId)
  )

  useEffect(() => {
    return () => {
      clearInterval(increment.current)
      clearInterval(intervalPdfs.current)
    }
  }, [])

  useEffect(() => {
    fetchInvoices()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  useEffect(() => {
    if (additionalPaymentSettings && periodId === '' && !isFetchingSettings && !generating) {
      const periodIdTemp = hash !== '' ? hash.toString().split('#')[1] : additionalPaymentSettings?.selectedPeriodId
      setPeriodId(periodIdTemp as string)
    }
    if (additionalPaymentSettings?.status > milkPaymentStatus.generatingStatus && generating && !isFetchingSettings) {
      const selectedPeriod = additionalPaymentSettings?.periods?.find(period => period.id === parseInt(periodId))
      const selectedPeriodParsed = selectedPeriod ? `${dayjs(selectedPeriod?.dateFrom).format('DD.MM.YYYY')} - ${dayjs(selectedPeriod?.dateTo).format('DD.MM.YYYY')}` : ''
      notify(t('The additional payment statements for the period: {{selectedMonth}} have been successfully created and can now be verified.', { selectedMonth: selectedPeriodParsed }), 'success')
      setGenerating(false)
      clearInterval(increment.current)
    } else if (additionalPaymentSettings?.status === milkPaymentStatus.generatingStatus && !increment.current) {
      increment.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    }

    //generate pdf files
    if (
      additionalPaymentSettings?.status >= milkPaymentStatus.verificationStatus &&
      additionalPaymentSettings.generatePdfJob &&
      !intervalPdfs.current
    ) {
      intervalPdfs.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    } else if (additionalPaymentSettings?.status >= milkPaymentStatus.verificationStatus &&
      !isFetchingSettings &&
      !additionalPaymentSettings?.generatePdfJob &&
      intervalPdfs.current &&
      isLoadingPdfFiles
    ) {
      setIsLoadingPdfFiles(false)
      clearInterval(intervalPdfs.current)
    }
  }, [
    additionalPaymentSettings,
    isFetchingSettings,
    hash
  ])

  useEffect(() => {
    fetchSettings()
    fetchInvoices()
  }, [periodId, t])

  function handleDateFormating(date) {
    return date ? parseDate(date) : null
  }

  function renderPeriodFilter() {

    return <Dropdown
      selection
      selectOnBlur={false}
      placeholder={t('Select month')}
      value={periodId}
      options={additionalPaymentSettings?.periods?.map(period => ({ value: period?.id, text: `${period.name} (${dayjs(period.dateFrom).format('DD.MM.YYYY')} - ${dayjs(period.dateTo).format('DD.MM.YYYY')})` }))}
      onChange={(e, data) => setPeriodId(data.value as string)}
    />
  }

  function renderGenerateModal() {
    return <GenerateModal
      openGeneratingModal={openGeneratingModal}
      handleClose={() => setOpenGeneratingModal(false)}
      generating={generating}
      handleGenerateInvoice={(data) => onSubmitGenerateForm(data)}
      milkTypes={additionalPaymentSettings?.milkTypes || []}
    />
  }

  function renderGenerateFileLoader() {
    return <>
      <LazyLottie
        getAnimationData={() => import('@assets/json-lottie/file-transfer.json')}
        loop
        id='file-transfer'
        style={{ height: 250, width: 250 }}
      />
      <div style={{ color: 'black' }}>{t('Please be patient, the milk bill statements are currently being prepared.')}</div>
    </>
  }

  function renderSteps() {
    return <Step.Group stackable='tablet' columns={2} widths={'5'}>
      {steps.map((step, index) => {
        const lastStep = index === steps.length - 1
        const statusActive = additionalPaymentSettings?.status === step.statusValue
        const completedStatus = lastStep ? additionalPaymentSettings?.status === step.statusValue : additionalPaymentSettings?.status > step.statusValue
        return <Step
          style={{ height: 75 }}
          completed={completedStatus}
          active={statusActive}
          key={step.description}
        >
          {(!statusActive || lastStep) && <Icon
            name={completedStatus ? 'checkmark' : 'close'}
            style={{ color: completedStatus ? 'green' : 'red' }}
          />}
          <Step.Content>
            <Step.Title>{completedStatus ? step.titleAfterFinished : step.title}</Step.Title>
            {statusActive && <StepDescr>
              {step.description}
            </StepDescr>}
          </Step.Content>
        </Step>
      })}
    </Step.Group>
  }

  async function handleGenerateMgaPdfs() {
    try {
      setIsOpenGeneratingPdfs(false)
      setIsLoadingPdfFiles(true)
      await MilkMoneyAccountingService.generateAdditionalPdfFiles({
        periodId
      })
      intervalPdfs.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    } catch (error) {
      setIsLoadingPdfFiles(false)
      clearInterval(intervalPdfs.current)
      handleErrors(error)
    }
  }

  function renderGenerateMgaPdf(isLoadingFiles) {
    if (isLoadingFiles) {
      return <ProgressWrapper style={{ width: 202, height: 37 }}>
        <Progress
          active
          indicating
          percent={additionalPaymentSettings?.generatePdfJob?.percentage || 0}
          progress
        />
      </ProgressWrapper>
    }

    return <Button
      buttonType={'popup'}
      labelPosition='left'
      onClick={() => handleGenerateMgaPdfs()}
      icon
    >
      <Icon name={'server'} style={{ color: 'white' }} />
      {'MG-Nachzahlung PDF erstellen'}
    </Button>
  }

  function renderActions() {
    const noRowsInTable = additionalPaymentSettings?.status >= milkPaymentStatus.verificationStatus && rows?.length === 0 && !loading && !isBusy
    const displayBankFileAction = additionalPaymentSettings?.status === milkPaymentStatus.verificationStatus
    const closeMonth = additionalPaymentSettings?.status === milkPaymentStatus.preclosingStatus
    const exportFile = additionalPaymentSettings?.status >= milkPaymentStatus.preclosingStatus
    const displayGenerateFiles = additionalPaymentSettings?.status >= milkPaymentStatus.verificationStatus ?
      (additionalPaymentSettings?.file ? additionalPaymentSettings?.status < milkPaymentStatus.closedStatus : true) : false

    const displayDownloadAll = additionalPaymentSettings?.status >= milkPaymentStatus.verificationStatus && Boolean(additionalPaymentSettings?.file)
    const isLoadingFiles = isLoadingPdfFiles || Boolean(additionalPaymentSettings?.generatePdfJob)

    return <ActionsWrapper>
      {displayGenerateFiles && !noRowsInTable && renderGenerateMgaPdf(isLoadingFiles)}
      {displayDownloadAll && !noRowsInTable && <ExcelExport
        url={'milk-money/milk-payment/additional/invoices/preview/pdf'}
        fileName={'pdfs'}
        downloadWithFileName
        disabled={isLoadingFiles}
        data={{ periodId }}
        icon={'cloud download'}
        name={t('Download MGA PDF')}
      />}
      {createNewAdditionalPaymentStatus && <Button
        loading={generating}
        disabled={generating}
        onClick={() => setOpenGeneratingModal(true)}
      >{t('Generate additional payment')}</Button>}
      {displayBankFileAction && !noRowsInTable && <Popup
        content={'Generierung der SEPA XML Datei für alle Milchgeldabrechnungen in diesem Abrechnungsmonat.'}
        trigger={<Button
          loading={generating}
          disabled={generating || Boolean(additionalPaymentSettings.hasErrors)}
          buttonType={'popup'}
          labelPosition='left'
          icon
          onClick={() => setExportBankModal(true)}
        >
          <Icon name={'euro sign'} style={{ color: 'white' }} />
          {'SEPA XML erstellen'}
        </Button>
        }
      />}
      {exportFile && !noRowsInTable && <ExcelExport
        url={'milk-money/milk-payment/additional/bank-file/sepa/export'}
        data={{ periodId }}
        icon={'cloud download'}
        fileName={'sepa_xml'}
        name={'SEPA XML'}
      />}
      {closeMonth && !noRowsInTable && <Button
        onClick={() => setIsOpenCloseMonthDialog(true)}
      >{t('Close additional month')}</Button>}
      <Report
        verificationStatus={additionalPaymentSettings?.status >= milkPaymentStatus.verificationStatus}
        periodId={periodId}
      />
    </ActionsWrapper>
  }

  function renderFilters() {
    return <FilterWrapper>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    </FilterWrapper>
  }

  async function handleRecalculateInvoiceFile(row, type) {
    setLoadingAction({
      type: type,
      id: row.id,
      value: true,
      idKey: 'id'
    })
    try {
      await MilkMoneyAccountingService.generateAdditionalInvoice(row.id)
      notify(t('The additional payment for the farmer {{supplierNo}} was successfully recalculated and created.', { supplierNo: row?.supplierNo }), 'success')
      fetchSettings()
      fetchInvoices()
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  async function handlePreview(id) {
    setLoadingAction({
      type: 'preview',
      id: id,
      value: true,
      idKey: 'id'
    })
    await fetch(`${ENV.API_URL}/milk-money/milk-payment/additional/invoices/${id}/preview/pdf`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') }
      }
    ).then(response => response.blob()).then(response => {
      const csvURL = window.URL.createObjectURL(response);
      setPreviewUrl(csvURL)
    })
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row: FarmerInvoiceType, type) {
    switch (type) {
      case 'preview':
        return handlePreview(row.id)
      case 'recalculate':
        return handleRecalculateInvoiceFile(row, type)
      default:
        return
    }
  }

  function renderTable() {
    const editAvailable = additionalPaymentSettings?.status === milkPaymentStatus.verificationStatus
    const actionsFiltered = editAvailable ? actions : actions.filter(action => action.type !== 'link').filter(action => action.actionType !== 'recalculate')

    return <>
      <div style={{ marginBottom: 10, marginTop: 20 }}>
        {renderFilters()}
      </div>
      <TableComponent
        isFetchingData={isFetchingData}
        animated
        loading={isFetchingSettings || isBusySettings}
        loadingAction={loadingAction}
        actions={actionsFiltered}
        tableColumnHeaders={tableColumnHeaders}
        dataFiltered={dataFiltered}
        handleClickAction={(row, type) => handleActionClick(row, type)}
        handleChangeDataFiltered={(data, tableColumnHeaders) => {
          setDataFiltered(data)
          setTableColumnHeaders(tableColumnHeaders)
        }}
        rows={rows}
        total={filtered}
      />
    </>
  }

  async function onSubmitGenerateForm(data) {
    try {
      setGenerating(true)
      data.milkTypes.forEach(milkType => {
        milkType.price = milkType?.price?.toString()?.replace(',', '.')
      })
      const response = await MilkMoneyAccountingService.generateAdditionalInvoices({
        ...data,
        dateFrom: handleDateFormating(data.dateFrom),
        dateTo: handleDateFormating(data.dateTo),
        date: handleDateFormating(data.date)
      })
      setPeriodId(response.data)
      setOpenGeneratingModal(false)
      fetchSettings()
      increment.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    } catch (error) {
      setGenerating(false)
      clearInterval(increment.current)
      handleErrors(error)
    }
  }

  const { filtered, rows } = additionalInvoices ? additionalInvoices : { filtered: 0, rows: [] };

  const generateFormStep = additionalPaymentSettings?.status === milkPaymentStatus.defaultStatus

  function renderMessage() {
    return <Message
      info
      style={{
        borderRadius: '10px',
        marginLeft: 'auto',
        marginRight: 'auto',
        width: 'fit-content',
        textAlign: 'center',
        marginTop: 42
      }}>
      {'Derzeit stehen keine Zeiträume zur Auswahl zur Verfügung. Bitte klicken Sie auf “MG-Nachzahlung generieren“, um eine neue Milchgeld-Nachzahlung zu erstellen.'}
    </Message>
  }

  function renderPreviewModal() {
    return <Dialog
      isDialogOpen={Boolean(previewUrl)}
      textHeader={t('Preview')}
      textClose={t('Close')}
      heightContent={'80vh'}
      type={'preview'}
      textContent={<iframe width='100%' height='100%' src={previewUrl}></iframe>}
      handleClose={() => setPreviewUrl('')}
    />
  }

  async function handleGenerateBankFile(data) {
    setExportBankModal(false)
    setIsBusySepaXML(true)
    try {
      await MilkMoneyAccountingService.generateAdditionalBankFile({ ...data, periodId })
      await fetchSettings()
      notify(t('The SEPA XML file for the period: {{month}} has been successfully created and is available for download.', { month: selectedPeriodParsed }), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusySepaXML(false)
  }

  function renderCloseMonthModal() {
    return <CloseMonth
      isOpenCloseMonthDialog={isOpenCloseMonthDialog}
      handleClose={(submitted?) => {
        if (submitted) {
          fetchSettings()
        }
        setIsOpenCloseMonthDialog(false)
      }}
      selectedPeriodId={periodId}
      selectedPeriod={selectedPeriodParsed}
    />
  }

  const selectedPeriod = additionalPaymentSettings?.periods?.find(period => period.id === parseInt(periodId))
  const selectedPeriodParsed = selectedPeriod ? `${dayjs(selectedPeriod?.dateFrom).format('DD.MM.YYYY')} - ${dayjs(selectedPeriod?.dateTo).format('DD.MM.YYYY')}` : ''
  const loading = isBusySettings || isBusy || isFetchingSettings
  const createNewAdditionalPaymentStatus = additionalPaymentSettings?.status === milkPaymentStatus.defaultStatus ||
    additionalPaymentSettings?.status === milkPaymentStatus.closedStatus

  return <Layout
    title={'Milchgeld Nachzahlungen'}
    filters={additionalPaymentSettings?.periods?.length > 0 && renderPeriodFilter()}
    isBusy={loading}
    useSpinner={isFetchingSettings}
    actions={!lighUserLogged && renderActions()}
    segmented
    animatedSpinner={isBusySepaXML ? {
      fileName: 'euro-bank-to-bank-transfer',
      text: t('Please be patient, the SEPA XML file with the booking data is currently being created.')
    } : null}
  >
    {renderCloseMonthModal()}
    {renderPreviewModal()}
    {renderGenerateModal()}
    <CreateBankFile
      openModal={openExportBankModal}
      accounts={additionalPaymentSettings?.accounts || []}
      handleClose={(submitted = false) => {
        setExportBankModal(false)
        if (submitted) {
          fetchSettings()
        }
      }}
      header={`SEPA XML für MG-Nachzahlung ${selectedPeriod?.name} (${selectedPeriodParsed})`}
      selectedMonth={{ label: selectedPeriodParsed, key: '' }}
      handleSubmit={(data) => handleGenerateBankFile(data)}
    />
    <div>
      {renderSteps()}
      {generateFormStep && !openGeneratingModal && renderMessage()}
      {(generatingFile || generating) && <div style={{ position: 'relative', height: 'calc(100vh - 700px)' }}>
        <StyledDimmer>
          {renderGenerateFileLoader()}
        </StyledDimmer>
      </div>}
    </div>
    {additionalInvoices && invoiceInDefaultStatus && renderTable()}
  </Layout>
}
export default AdditionalPayment